import { DateTimeEditor, DropdownEditor, Field, TextEditor } from '@approvalmax/ui';
import { dataProviders } from 'modules/common';
import { domain } from 'modules/data';
import { PlainDataProvider } from 'modules/data-providers';
import { memo } from 'react';

import { PLACEHOLDER_FIELD } from '../../RequestSearchPopup.constants';
import { DateFieldRow, DateRow, WrapFieldRow } from '../../RequestSearchPopup.styles';
import { messages } from './QBOSearchFields.messages';
import { QBOSearchFieldsProps } from './QBOSearchFields.types';

export const QBOSearchFields = memo<QBOSearchFieldsProps>((props) => {
    const { company, integrationCode, integrationEnabled, params, updateParameter, disabled } = props;

    const isVendor = integrationCode === domain.IntegrationCode.QBooksVendor;
    const isExpense = integrationCode === domain.IntegrationCode.QBooksExpense;
    const isPO = integrationCode === domain.IntegrationCode.QBooksPo;
    const isJournalEntry = integrationCode === domain.IntegrationCode.QBooksJournalEntry;
    const isSalesInvoice = integrationCode === domain.IntegrationCode.QBooksInvoice;

    const vendorField = (
        <Field title={messages.qbooksVendorFieldTitle}>
            <dataProviders.FieldDataProvider
                key={params.company?.id}
                integrationCode={integrationCode}
                field={
                    params.company?.fields.find((f) => f.systemPurpose === domain.FieldSystemPurpose.QBooksVendor) ||
                    PLACEHOLDER_FIELD
                }
            >
                <DropdownEditor
                    placeholder={messages.qbooksVendorPlaceholder}
                    value={params.qbooksVendor}
                    onChange={updateParameter('qbooksVendor')}
                    disabled={disabled || !integrationEnabled || isVendor}
                />
            </dataProviders.FieldDataProvider>
        </Field>
    );

    const payeeVendorField = (
        <Field title={messages.qbooksPayeeVendorFieldTitle}>
            <dataProviders.FieldDataProvider
                key={params.company?.id}
                integrationCode={integrationCode}
                field={
                    params.company?.fields.find(
                        (f) => f.systemPurpose === domain.FieldSystemPurpose.QBooksPayeeVendor
                    ) || PLACEHOLDER_FIELD
                }
            >
                <DropdownEditor
                    placeholder={messages.qbooksPayeeVendorPlaceholder}
                    value={params.qbooksPayeeVendor}
                    onChange={updateParameter('qbooksPayeeVendor')}
                    disabled={disabled || !integrationEnabled}
                />
            </dataProviders.FieldDataProvider>
        </Field>
    );

    const renderCustomerField = () => {
        const field = params.company?.fields.find((f) =>
            isSalesInvoice
                ? f.systemPurpose === domain.FieldSystemPurpose.QBooksInvoiceCustomer
                : f.systemPurpose === domain.FieldSystemPurpose.QBooksCustomer
        );

        if (!field) {
            return undefined;
        }

        return (
            <Field title={messages.qbooksCustomerFieldTitle}>
                <dataProviders.FieldDataProvider
                    key={params.company?.id}
                    field={field}
                    integrationCode={integrationCode}
                >
                    <DropdownEditor
                        placeholder={messages.qbooksCustomerPlaceholder}
                        disabled={disabled || !integrationEnabled || isVendor}
                        value={params.qbooksCustomer}
                        onChange={updateParameter('qbooksCustomer')}
                    />
                </dataProviders.FieldDataProvider>
            </Field>
        );
    };

    const payeeCustomerField = (
        <Field title={messages.qbooksPayeeCustomerFieldTitle}>
            <dataProviders.FieldDataProvider
                key={params.company?.id}
                integrationCode={integrationCode}
                field={
                    params.company?.fields.find(
                        (f) => f.systemPurpose === domain.FieldSystemPurpose.QBooksPayeeCustomer
                    ) || PLACEHOLDER_FIELD
                }
            >
                <DropdownEditor
                    placeholder={messages.qbooksPayeeCustomerPlaceholder}
                    value={params.qbooksPayeeCustomer}
                    onChange={updateParameter('qbooksPayeeCustomer')}
                    disabled={disabled || !integrationEnabled}
                />
            </dataProviders.FieldDataProvider>
        </Field>
    );

    const employeeField = (
        <Field title={messages.qbooksEmployeeFieldTitle}>
            <dataProviders.FieldDataProvider
                key={params.company?.id}
                integrationCode={integrationCode}
                field={
                    params.company?.fields.find((f) => f.systemPurpose === domain.FieldSystemPurpose.QBooksEmployee) ||
                    PLACEHOLDER_FIELD
                }
            >
                <DropdownEditor
                    placeholder={messages.qbooksEmployeePlaceholder}
                    value={params.qbooksEmployee}
                    onChange={updateParameter('qbooksEmployee')}
                    disabled={disabled || !integrationEnabled}
                />
            </dataProviders.FieldDataProvider>
        </Field>
    );

    const payeeEmployeeField = (
        <Field title={messages.qbooksPayeeEmployeeFieldTitle}>
            <dataProviders.FieldDataProvider
                key={params.company?.id}
                integrationCode={integrationCode}
                field={
                    params.company?.fields.find(
                        (f) => f.systemPurpose === domain.FieldSystemPurpose.QBooksPayeeEmployee
                    ) || PLACEHOLDER_FIELD
                }
            >
                <DropdownEditor
                    placeholder={messages.qbooksPayeeEmployeePlaceholder}
                    value={params.qbooksPayeeEmployee}
                    onChange={updateParameter('qbooksPayeeEmployee')}
                    disabled={disabled || !integrationEnabled}
                />
            </dataProviders.FieldDataProvider>
        </Field>
    );

    return (
        <>
            <WrapFieldRow>
                <Field title={messages.qbooksRequestorFieldTitle}>
                    <PlainDataProvider items={company?.allMembers || []} filterAttribute='displayName'>
                        <DropdownEditor
                            displayAttribute='displayName'
                            placeholder={messages.qbooksRequestorPlaceholder}
                            value={params.requestor}
                            onChange={updateParameter('requestor')}
                            disabled={disabled}
                        />
                    </PlainDataProvider>
                </Field>

                <Field title={messages.qbooksPoNumberFieldTitle}>
                    <TextEditor
                        placeholder={messages.qbooksPoNumberPlaceholder}
                        disabled={disabled || isVendor}
                        value={params.qbooksPoNumber}
                        onChange={updateParameter('qbooksPoNumber')}
                    />
                </Field>

                {!isExpense && !isJournalEntry && !isSalesInvoice && vendorField}

                {!isJournalEntry && (
                    <Field title={messages.qbooksProductFieldTitle}>
                        <dataProviders.FieldDataProvider
                            key={params.company?.id}
                            integrationCode={integrationCode}
                            field={
                                params.company?.fields.find((field) =>
                                    isSalesInvoice
                                        ? field.systemPurpose === domain.FieldSystemPurpose.QBooksSalesItem
                                        : field.systemPurpose === domain.FieldSystemPurpose.QBooksPoItem
                                ) || PLACEHOLDER_FIELD
                            }
                        >
                            <DropdownEditor
                                placeholder={messages.qbooksProductPlaceholder}
                                value={params.qbooksProduct}
                                onChange={updateParameter('qbooksProduct')}
                                disabled={disabled || !integrationEnabled || isVendor}
                            />
                        </dataProviders.FieldDataProvider>
                    </Field>
                )}

                {isExpense && (
                    <>
                        <Field title={null}>
                            <div />
                        </Field>

                        {payeeVendorField}

                        {payeeCustomerField}

                        {payeeEmployeeField}

                        <Field title={messages.qbooksPaymentAccountFieldTitle}>
                            <dataProviders.FieldDataProvider
                                key={params.company?.id}
                                integrationCode={integrationCode}
                                field={
                                    params.company?.fields.find(
                                        (f) => f.systemPurpose === domain.FieldSystemPurpose.QBooksPaymentAccount
                                    ) || PLACEHOLDER_FIELD
                                }
                            >
                                <DropdownEditor
                                    placeholder={messages.qbooksPaymentAccountPlaceholder}
                                    value={params.qbooksPaymentAccount}
                                    onChange={updateParameter('qbooksPaymentAccount')}
                                    disabled={disabled || !integrationEnabled}
                                />
                            </dataProviders.FieldDataProvider>
                        </Field>
                    </>
                )}

                <Field title={messages.qbooksAccountFieldTitle}>
                    <dataProviders.FieldDataProvider
                        key={params.company?.id}
                        integrationCode={integrationCode}
                        field={
                            params.company?.fields.find(
                                (f) => f.systemPurpose === domain.FieldSystemPurpose.QBooksAccount
                            ) || PLACEHOLDER_FIELD
                        }
                    >
                        <DropdownEditor
                            placeholder={messages.qbooksAccountPlaceholder}
                            value={params.qbooksAccount}
                            onChange={updateParameter('qbooksAccount')}
                            disabled={disabled || !integrationEnabled || isVendor}
                        />
                    </dataProviders.FieldDataProvider>
                </Field>

                {!isJournalEntry && renderCustomerField()}

                {isJournalEntry && vendorField}

                {isJournalEntry && renderCustomerField()}

                {isJournalEntry && employeeField}

                {(() => {
                    const field = params.company?.fields.find(
                        (f) => f.systemPurpose === domain.FieldSystemPurpose.QBooksClass
                    );

                    if (!field) {
                        return undefined;
                    }

                    return (
                        <Field title={messages.qbooksClassFieldTitle}>
                            <dataProviders.FieldDataProvider field={field} integrationCode={integrationCode}>
                                <DropdownEditor
                                    placeholder={messages.qbooksClassPlaceholder}
                                    disabled={disabled || !integrationEnabled || isVendor}
                                    value={params.qbooksClass}
                                    onChange={updateParameter('qbooksClass')}
                                />
                            </dataProviders.FieldDataProvider>
                        </Field>
                    );
                })()}

                {(() => {
                    const field = params.company?.fields.find(
                        (f) => f.systemPurpose === domain.FieldSystemPurpose.QBooksDepartment
                    );

                    if (!field) {
                        return undefined;
                    }

                    return (
                        <Field title={messages.qbooksDepartmentFieldTitle}>
                            <dataProviders.FieldDataProvider field={field} integrationCode={integrationCode}>
                                <DropdownEditor
                                    placeholder={messages.qbooksDepartmentPlaceholder}
                                    disabled={disabled || !integrationEnabled || isVendor}
                                    value={params.qbooksDepartment}
                                    onChange={updateParameter('qbooksDepartment')}
                                />
                            </dataProviders.FieldDataProvider>
                        </Field>
                    );
                })()}

                {isJournalEntry && (
                    <Field title={messages.qbooksCurrencyFieldTitle}>
                        <dataProviders.FieldDataProvider
                            key={params.company?.id}
                            integrationCode={integrationCode}
                            field={
                                params.company?.fields.find(
                                    (f) => f.systemPurpose === domain.FieldSystemPurpose.QBooksCurrency
                                ) || PLACEHOLDER_FIELD
                            }
                        >
                            <DropdownEditor
                                placeholder={messages.qbooksCurrencyPlaceholder}
                                value={params.qbooksCurrency}
                                onChange={updateParameter('qbooksCurrency')}
                                disabled={disabled}
                            />
                        </dataProviders.FieldDataProvider>
                    </Field>
                )}

                {isPO && (
                    <Field title={messages.qbooksPOStatusFieldTitle}>
                        <dataProviders.FieldDataProvider
                            integrationCode={integrationCode}
                            field={
                                params.company?.fields.find(
                                    (f) => f.systemPurpose === domain.FieldSystemPurpose.QBooksDocumentStatus
                                ) || PLACEHOLDER_FIELD
                            }
                        >
                            <DropdownEditor
                                placeholder={messages.qbooksPOStatusPlaceholder}
                                disabled={disabled || !integrationEnabled || isVendor}
                                value={params.qbooksDocumentStatus}
                                onChange={updateParameter('qbooksDocumentStatus')}
                            />
                        </dataProviders.FieldDataProvider>
                    </Field>
                )}
            </WrapFieldRow>

            <DateRow>
                <Field title={messages.dateFieldTitle}>
                    <DateFieldRow>
                        <div>{messages.dateFromLabel}</div>

                        <DateTimeEditor
                            placeholder={messages.dateGreaterEqualsPlaceholder}
                            disabled={disabled || isVendor}
                            value={params.documentDateGreaterEquals}
                            onChange={updateParameter('documentDateGreaterEquals')}
                        />

                        <div>{messages.dateToLabel}</div>

                        <DateTimeEditor
                            placeholder={messages.dateLessEqualsPlaceholder}
                            disabled={disabled || isVendor}
                            value={params.documentDateLessEquals}
                            onChange={updateParameter('documentDateLessEquals')}
                        />
                    </DateFieldRow>
                </Field>
            </DateRow>
        </>
    );
});

QBOSearchFields.displayName = 'QBOSearchFields';
